import { FC, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { PersonalIdentifierType } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';

import { DiagnosticStatus } from './DiagnosticCard/DiagnosticCounter';
import { InterwillDiagnosticCard } from './DiagnosticCard';

const getStatus = (count: number) => {
  if (count === 0) {
    return DiagnosticStatus.NOT_READY;
  }
  return DiagnosticStatus.READY;
};

type InterwillDocumentDiagnosticCardProps = {
  onCreatePress: () => void;
};

export const InterwillDocumentDiagnosticCard: FC<InterwillDocumentDiagnosticCardProps> = ({
  onCreatePress,
}) => {
  const { t } = useTranslation('pages/pageInterwill', {
    keyPrefix: 'diagnosticCards.documents',
  });

  const { collections } = useSJDatabase();

  const count = useMemo(
    () =>
      collections.personalIdentifiersDocuments?.findMany(
        (dto) =>
          (dto.type === DocumentType.PASSPORT || dto.type === DocumentType.PERSONAL_IDENTIFIERS) &&
          (dto.personalIdentifierType === PersonalIdentifierType.PASSPORT ||
            dto.personalIdentifierType === PersonalIdentifierType.NATIONAL_ID) &&
          dto.beneficiaryId === 'me',
      ).length ?? 0,
    [collections.beneficiaries],
  );

  const status = getStatus(count);

  return (
    <InterwillDiagnosticCard
      buttonText={t('buttonText')}
      content={<Content status={status} />}
      count={count}
      iconName="identification-card"
      status={status}
      title={t('title')}
      onBtnClick={onCreatePress}
    />
  );
};

const Content: FC<{ status: DiagnosticStatus }> = ({ status }) => {
  const { t } = useTranslation('pages/pageInterwill', {
    keyPrefix: 'diagnosticCards.documents.content',
  });
  const { t: td } = useTranslation('documents', {
    keyPrefix: 'enums.docCategory',
  });

  const getText = () => {
    if (status === DiagnosticStatus.NOT_READY) {
      return t('primaryText.notReady');
    }

    return t('primaryText.ready');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <span>{getText()}</span>

      {status !== DiagnosticStatus.READY && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>
            &#x2022; <b>{td('nationalId')} </b> {t('secondaryText.or')} <b>{td('passport')}</b>
          </span>
        </div>
      )}
    </div>
  );
};
